/** Install an app for your organization: plan → set up → pay → installing → ready. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { topupOrgWallet } from '@/api/billing'
import {
  fmtDate, getApp, getInstall, getQuote, installApp, money, mktError,
  type Basis, type Quote, type Subscription,
} from '@/api/marketplace'
import { useRazorpayCheckout } from '@/hooks/useRazorpayCheckout'
import MarketplaceShell, { AppTile, Check, Stepper, Steps } from './MarketplaceShell'

const LABELS = ['Plan', 'Set up', 'Payment', 'Done']

export default function InstallPage() {
  const { slug = '' } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { checkout, busy: paying } = useRazorpayCheckout()

  const [step, setStep] = useState(1)
  const [basis, setBasis] = useState<Basis>((params.get('basis') as Basis) || 'person')
  const [people, setPeople] = useState(Math.max(1, Number(params.get('people')) || 10))
  const [size, setSize] = useState<string | null>(params.get('size'))
  const [quote, setQuote] = useState<Quote | null>(null)
  const [quoteError, setQuoteError] = useState<string | null>(null)
  const [sample, setSample] = useState(true)
  const [inviteText, setInviteText] = useState('')
  const [invites, setInvites] = useState<string[]>([])
  const [agree, setAgree] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sub, setSub] = useState<Subscription | null>(null)
  const [servers, setServers] = useState<{ id: string; name: string; max_people: number; price_minor: number }[]>([])
  const [hasPerson, setHasPerson] = useState(true)

  const loadQuote = useCallback(() => {
    setQuoteError(null)
    getQuote(slug, basis, basis === 'person' ? people : undefined, basis === 'server' ? size || undefined : undefined)
      .then(setQuote)
      .catch((e) => setQuoteError(mktError(e).message))
  }, [slug, basis, people, size])

  useEffect(() => {
    const t = window.setTimeout(loadQuote, 200)
    return () => window.clearTimeout(t)
  }, [loadQuote])

  // The app's offered plans (for the plan step).
  useEffect(() => {
    getApp(slug).then((a) => {
      setServers(a.server)
      setHasPerson(!!a.person)
      if (!a.person && basis === 'person' && a.server.length) setBasis('server')
      if (!size && a.server.length) setSize(a.server[Math.min(1, a.server.length - 1)].id)
    }).catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  // Poll the install until it settles.
  const poll = useRef<number | null>(null)
  useEffect(() => {
    if (!sub || sub.status !== 'installing') return
    poll.current = window.setTimeout(() => {
      getInstall(sub.id).then(setSub).catch(() => undefined)
    }, 1200)
    return () => { if (poll.current) window.clearTimeout(poll.current) }
  }, [sub])

  const cap = basis === 'person'
    ? Math.min(people, quote?.trial?.people ?? people)
    : (servers.find((s) => s.id === size)?.max_people ?? 0)
  const addInvite = () => {
    const v = inviteText.trim().toLowerCase()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error('Enter a valid email address'); return }
    if (invites.includes(v)) { setInviteText(''); return }
    if (cap && invites.length + 1 >= cap) { toast.error(`Your plan has ${cap} places, including you.`); return }
    setInvites([...invites, v]); setInviteText('')
  }

  const shortfall = quote ? Math.max(0, quote.due_today_minor - (quote.wallet_balance_minor ?? 0)) : 0

  const topUp = async () => {
    if (!quote || shortfall <= 0) return
    const result = await checkout({
      purpose: 'org_wallet_topup',
      description: 'Organization wallet top-up',
      precreate: () => topupOrgWallet(shortfall, quote.currency),
    })
    if (result.cancelled) return
    if (!result.ok) { toast.error(result.error || 'The payment did not go through.'); return }
    toast.success('Money added to your organization wallet')
    loadQuote()
  }

  const install = async () => {
    if (!quote) return
    setSubmitting(true)
    try {
      const s = await installApp({ slug, basis, seats: basis === 'person' ? people : undefined, size: basis === 'server' ? size || undefined : undefined, sample_data: sample, invites })
      setSub(s)
      setStep(4)
    } catch (e) {
      const err = mktError(e)
      if (err.code === 'MARKETPLACE_WALLET_SHORT') { toast.error(err.message); loadQuote() } else toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const app = quote?.app
  const blocked = quote && (!quote.installer_role || quote.own_app || quote.existing_subscription_id)

  return (
    <MarketplaceShell>
      <div className="mkt-wrap" style={{ padding: '24px 20px 48px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {step <= 4 && !(sub && sub.status !== 'installing') && <Steps labels={LABELS} current={step} />}
        {quoteError && !quote && (
          <div className="mkt-empty"><strong>{quoteError}</strong><Link to="/marketplace" className="btn btn-primary">Back to the marketplace</Link></div>
        )}

        {quote && blocked && (
          <div className="mkt-panel mkt-col" style={{ maxWidth: 640, gap: 14 }}>
            {quote.own_app ? (
              <><h1 style={{ fontSize: 24 }}>This app belongs to your organization</h1><p className="mkt-muted" style={{ margin: 0 }}>You already have it — open it from the Workbench.</p><Link to="/workbench" className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>Open the Workbench</Link></>
            ) : quote.existing_subscription_id ? (
              <><h1 style={{ fontSize: 24 }}>Your organization already has {app?.name}</h1><p className="mkt-muted" style={{ margin: 0 }}>Manage its plan, people and billing from My apps.</p><Link to="/marketplace/my-apps" className="btn btn-primary" style={{ alignSelf: 'flex-start' }}>Go to My apps</Link></>
            ) : (
              <><h1 style={{ fontSize: 24 }}>Ask your administrator to install {app?.name}</h1>
                <p className="mkt-muted" style={{ margin: 0 }}>Apps are installed for your whole organization, so only its administrators (or project owners) can install and pay for them. Send them this link:</p>
                <div className="mkt-row"><code style={{ padding: '8px 10px', background: 'var(--color-raised, var(--color-bg))', borderRadius: 8, wordBreak: 'break-all' }}>{window.location.origin}/marketplace/app/{slug}</code>
                  <button type="button" className="btn btn-secondary" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/marketplace/app/${slug}`); toast.success('Link copied') }}>Copy link</button></div></>
            )}
          </div>
        )}

        {quote && !blocked && step < 4 && (
          <div className="mkt-split">
            <section className="mkt-main mkt-panel">
              {step === 1 && (
                <>
                  <div className="mkt-col" style={{ gap: 4 }}><h1 style={{ fontSize: 26 }}>Confirm your plan</h1><span className="mkt-muted">This is what you picked. You can change it now or any time later.</span></div>
                  {hasPerson && servers.length > 0 && (
                    <div className="mkt-choices" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <button type="button" aria-pressed={basis === 'person'} className={`mkt-choice${basis === 'person' ? ' on' : ''}`} onClick={() => setBasis('person')}><strong>Per person</strong><span className="mkt-muted" style={{ fontSize: 14 }}>A monthly price for each person. Best for small teams.</span></button>
                      <button type="button" aria-pressed={basis === 'server'} className={`mkt-choice${basis === 'server' ? ' on' : ''}`} onClick={() => setBasis('server')}><strong>Per server</strong><span className="mkt-muted" style={{ fontSize: 14 }}>One price for your whole team, with more room and speed.</span></button>
                    </div>
                  )}
                  {basis === 'person' ? (
                    <div className="mkt-row" style={{ justifyContent: 'space-between', background: 'var(--color-raised, var(--color-bg))', padding: 16, borderRadius: 14 }}>
                      <span>How many people will use {app?.name}?</span>
                      <Stepper value={people} onChange={setPeople} label="people" />
                    </div>
                  ) : (
                    <div role="radiogroup" aria-label="Server size" className="mkt-choices">
                      {servers.map((s) => (
                        <button key={s.id} type="button" role="radio" aria-checked={size === s.id} onClick={() => setSize(s.id)} className={`mkt-choice mkt-choice-row${size === s.id ? ' on' : ''}`}>
                          <span className="mkt-col" style={{ gap: 0 }}><strong>{s.name} server</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Up to {s.max_people} people</span></span>
                          <strong>{money(s.price_minor, quote.currency)}<span className="mkt-faint"> /mo</span></strong>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                    <Link to={`/marketplace/app/${slug}`} className="btn btn-secondary">Back to app</Link>
                    <button type="button" className="btn btn-primary btn-lg" onClick={() => setStep(2)}>Continue</button>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="mkt-col" style={{ gap: 4 }}><h1 style={{ fontSize: 26 }}>Set up your app</h1><span className="mkt-muted">Two quick choices. We'll do the rest.</span></div>
                  <div className="mkt-banner plain"><div><strong>For {quote.organization?.name || 'your organization'}</strong>{app?.name} gets its own private database — only people you invite can see its data.</div></div>
                  <button type="button" role="switch" aria-checked={sample} onClick={() => setSample(!sample)} className="mkt-choice mkt-choice-row" style={{ justifyContent: 'flex-start' }}>
                    <span className={`mkt-switch${sample ? ' on' : ''}`} aria-hidden="true" />
                    <span className="mkt-col" style={{ gap: 0 }}><strong>Start with example data</strong><span className="mkt-muted" style={{ fontSize: 14 }}>Handy for trying things out. If the app has no example data, it starts empty.</span></span>
                  </button>
                  <div className="mkt-field">
                    <label htmlFor="inv">Invite your team <span className="mkt-muted" style={{ fontWeight: 400 }}>(optional — you can do this later)</span></label>
                    <div className="mkt-row" style={{ flexWrap: 'nowrap' }}>
                      <input id="inv" type="email" value={inviteText} onChange={(e) => setInviteText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInvite() } }} placeholder="name@yourcompany.com" style={{ flex: 1 }} />
                      <button type="button" className="btn btn-secondary" onClick={addInvite}>Add</button>
                    </div>
                    <div className="mkt-row" style={{ gap: 8 }}>
                      {invites.map((e) => (
                        <span key={e} className="mkt-pill info" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{e}
                          <button type="button" aria-label={`Remove ${e}`} onClick={() => setInvites(invites.filter((x) => x !== e))} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 15 }}>×</button>
                        </span>
                      ))}
                    </div>
                    {cap > 0 && <span className="mkt-faint">You + {invites.length} invited = {invites.length + 1} of {cap} places{quote.trial && basis === 'person' && cap < people ? ' during the free trial' : ''}.</span>}
                  </div>
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Back</button>
                    <button type="button" className="btn btn-primary btn-lg" onClick={() => setStep(3)}>Continue to payment</button>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="mkt-col" style={{ gap: 4 }}>
                    <h1 style={{ fontSize: 26 }}>{quote.trial ? 'Start your free trial' : 'Pay for the first month'}</h1>
                    <span className="mkt-muted">{quote.trial
                      ? `Nothing is charged today. From ${fmtDate(quote.trial.ends_at)} each month's price is taken from your organization wallet.`
                      : "Each month's price is taken from your organization wallet. Top it up by UPI or card."}</span>
                  </div>
                  <div className="mkt-panel" style={{ padding: 16 }}>
                    <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                      <span>Organization wallet</span><strong style={{ fontSize: 20 }}>{money(quote.wallet_balance_minor ?? 0, quote.currency)}</strong>
                    </div>
                    {shortfall > 0 ? (
                      <div className="mkt-col" style={{ gap: 10, marginTop: 10 }}>
                        <div className="mkt-msg warn">Add {money(shortfall, quote.currency)} to pay for the first month (GST is added at payment).</div>
                        <button type="button" className="btn btn-primary" onClick={topUp} disabled={paying} style={{ alignSelf: 'flex-start' }}>{paying ? 'Opening payment…' : `Add ${money(shortfall, quote.currency)} with UPI or card`}</button>
                      </div>
                    ) : quote.trial ? (
                      <div className="mkt-faint" style={{ marginTop: 8 }}>Make sure it holds {money(quote.monthly_minor, quote.currency)} by {fmtDate(quote.trial.ends_at)} — we'll remind you.</div>
                    ) : (
                      <div className="mkt-msg ok" style={{ marginTop: 10 }}>Your wallet covers the first month.</div>
                    )}
                  </div>
                  <div className="mkt-row mkt-muted" style={{ gap: 8, fontSize: 14 }}>
                    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></svg>
                    Payments are processed securely by our payment partner. Twasta never sees your card or bank details.
                  </div>
                  <label className="mkt-row" style={{ gap: 10, alignItems: 'flex-start', cursor: 'pointer', flexWrap: 'nowrap' }}>
                    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ width: 20, height: 20, marginTop: 2 }} />
                    <span>I agree to be charged <strong>{money(quote.monthly_minor, quote.currency)}</strong> + GST every month {quote.trial ? `from ${fmtDate(quote.trial.ends_at)}` : 'starting today'} until I cancel, from my organization wallet.</span>
                  </label>
                  <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>Back</button>
                    <button type="button" className="btn btn-primary btn-lg" onClick={install} disabled={!agree || submitting || shortfall > 0}>
                      {submitting ? 'Starting…' : quote.trial ? 'Start my free trial' : `Pay ${money(quote.monthly_minor, quote.currency)} and install`}
                    </button>
                  </div>
                </>
              )}
            </section>

            <aside className="mkt-aside mkt-panel" aria-label="Order summary">
              <div className="mkt-row" style={{ flexWrap: 'nowrap' }}>
                {app && <AppTile color={app.icon_color} text={app.initials} />}
                <div className="mkt-col" style={{ gap: 0 }}><strong style={{ fontSize: 17 }}>{app?.name}</strong><span className="mkt-muted" style={{ fontSize: 14 }}>by {app?.publisher}</span></div>
              </div>
              <div className="mkt-col" style={{ gap: 8, borderTop: '1px solid var(--color-border)', paddingTop: 14 }}>
                <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">{basis === 'person' ? `${people} people × ${money(quote.unit_price_minor, quote.currency)}` : `${servers.find((s) => s.id === size)?.name ?? ''} server`}</span><span>{money(quote.monthly_minor, quote.currency)}</span></div>
                <div className="mkt-row" style={{ justifyContent: 'space-between', fontWeight: 700, fontSize: 17 }}><span>Every month</span><span>{money(quote.monthly_minor, quote.currency)} + GST</span></div>
              </div>
              {quote.trial ? (
                <div className="mkt-banner ok" style={{ padding: 12 }}><div><strong>Due today: {money(0, quote.currency)}</strong>{quote.trial.days}-day free trial for up to {quote.trial.people} people. First charge on {fmtDate(quote.trial.ends_at)}.</div></div>
              ) : (
                <div className="mkt-banner plain" style={{ padding: 12 }}><div><strong>Due today: {money(quote.due_today_minor, quote.currency)}</strong>No free trial right now. Cancel any time.</div></div>
              )}
              <ul className="mkt-col" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 6, fontSize: 14 }}>
                {quote.limits.slice(0, 3).map((l) => (<li key={l.key} className="mkt-row" style={{ gap: 8 }}><span style={{ color: 'var(--color-success)' }}><Check /></span>{l.label}: {l.value}</li>))}
                <li className="mkt-row" style={{ gap: 8 }}><span style={{ color: 'var(--color-success)' }}><Check /></span>Change plan or cancel from My apps</li>
              </ul>
            </aside>
          </div>
        )}

        {step === 4 && sub && (
          <InstallProgress sub={sub} onOpen={() => navigate('/marketplace/my-apps')} />
        )}
      </div>
    </MarketplaceShell>
  )
}

function InstallProgress({ sub, onOpen }: { sub: Subscription; onOpen: () => void }) {
  const steps = sub.install.steps || []
  const done = steps.filter((s) => s.state === 'done' || s.state === 'skipped').length
  const pct = steps.length ? Math.round((done / steps.length) * 100) : 0
  if (sub.status === 'failed') {
    return (
      <div className="mkt-panel mkt-col" style={{ maxWidth: 640, margin: '0 auto', gap: 14 }}>
        <h1 style={{ fontSize: 26 }}>We couldn't finish the install</h1>
        <div className="mkt-banner bad"><div>{sub.install.error || 'Something went wrong. Nothing was charged.'}</div></div>
        <div className="mkt-row"><Link to="/marketplace/my-apps" className="btn btn-primary">Go to My apps to try again</Link>{sub.support_email && <a className="btn btn-secondary" href={`mailto:${sub.support_email}`}>Contact the publisher</a>}</div>
      </div>
    )
  }
  if (sub.status === 'installing') {
    return (
      <section aria-live="polite" className="mkt-panel mkt-col" style={{ maxWidth: 640, margin: '0 auto', gap: 18 }}>
        <div className="mkt-col" style={{ alignItems: 'center', textAlign: 'center', gap: 6 }}>
          <AppTile color={sub.icon_color} text={sub.initials} size={60} />
          <h1 style={{ fontSize: 26 }}>Setting up {sub.name}</h1>
          <span className="mkt-muted">This usually takes under a minute. You can leave this page — we'll email you when it's ready.</span>
        </div>
        <div className="mkt-meter" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Install progress"><div style={{ width: `${pct}%` }} /></div>
        <ol className="mkt-col" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 12 }}>
          {steps.map((s) => (
            <li key={s.key} className="mkt-row" style={{ gap: 12, flexWrap: 'nowrap', color: s.state === 'pending' ? 'var(--color-text-faint)' : 'var(--color-text)', fontWeight: s.state === 'running' ? 600 : 400 }}>
              <span className="mkt-steps"><span className="dot" style={s.state === 'done' ? { background: 'var(--color-success)', color: '#fff', borderColor: 'var(--color-success)' } : undefined}>{s.state === 'done' ? '✓' : s.state === 'running' ? '…' : ''}</span></span>
              {s.label}
            </li>
          ))}
        </ol>
      </section>
    )
  }
  return (
    <section className="mkt-col" style={{ maxWidth: 720, margin: '0 auto', gap: 18, width: '100%' }}>
      <div className="mkt-panel mkt-col" style={{ alignItems: 'center', textAlign: 'center', gap: 14, padding: 36 }}>
        <span className="mkt-tile" style={{ width: 68, height: 68, borderRadius: 999, background: 'var(--color-success-weak)', color: 'var(--color-success)' }}><Check size={34} /></span>
        <h1 style={{ fontSize: 30 }}>{sub.name} is ready</h1>
        <p className="mkt-muted" style={{ margin: 0, maxWidth: 520 }}>
          {sub.status === 'trial' ? `Your free trial runs until ${fmtDate(sub.trial_ends_at)}. Nothing has been charged.`
            : sub.status === 'past_due' ? `The app is ready, but your wallet couldn't cover the first month. Add money before ${fmtDate(sub.grace_until)} to keep using it.`
              : 'Your first month is paid. A receipt is in My apps.'}
        </p>
        <div className="mkt-row" style={{ justifyContent: 'center' }}>
          <a href={sub.open_url} className="btn btn-primary btn-lg" target="_blank" rel="noreferrer">Open {sub.name}</a>
          <button type="button" className="btn btn-secondary btn-lg" onClick={onOpen}>Go to My apps</button>
        </div>
      </div>
      {steps.some((s) => s.note) && (
        <div className="mkt-panel mkt-col" style={{ gap: 6 }}>
          {steps.filter((s) => s.note).map((s) => <span key={s.key} className="mkt-muted" style={{ fontSize: 14 }}><strong style={{ color: 'var(--color-text)' }}>{s.label}:</strong> {s.note}</span>)}
        </div>
      )}
    </section>
  )
}
